// src/components/Issues/StatusDialog.js
import React, { useState, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  Button,
  CircularProgress,
  TextField,
  Box,
} from '@mui/material';
import { Autocomplete } from '@mui/material';

const StatusDialog = ({
  open,
  selectedIssue,
  newStatus,
  description = '',
  statusOptions,
  processingId,
  onClose,
  onStatusChange,
  onNewStatusChange,
  onDescriptionChange,
}) => {
  const [searchInput, setSearchInput] = useState('');

  // Filter statuses based on search input
  const filteredStatuses = useMemo(() => {
    if (!searchInput) return statusOptions;
    
    const searchTerm = searchInput.toLowerCase();
    return statusOptions.filter(status => 
      status.name?.toLowerCase().includes(searchTerm)
    );
  }, [statusOptions, searchInput]);

  // Get the selected status object
  const getSelectedStatusObject = () => {
    return statusOptions.find(status => status.id === newStatus) || null;
  };

  const handleStatusChange = (event, value) => {
    onNewStatusChange(value ? value.id : '');
  };

  const handleInputChange = (event, value) => {
    setSearchInput(value);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Update Issue Status</DialogTitle>
      <DialogContent>
        <FormControl fullWidth sx={{ mt: 2 }}>
          <Autocomplete
            options={filteredStatuses}
            getOptionLabel={(option) => option.name || 'Unknown Status'}
            value={getSelectedStatusObject()}
            onChange={handleStatusChange}
            onInputChange={handleInputChange}
            inputValue={searchInput}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Search and Select Status"
                placeholder="Type to search statuses..."
                variant="outlined"
              />
            )}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            noOptionsText="No statuses found"
          />
        </FormControl>
        <Box sx={{ mt: 2 }}>
          <TextField
            fullWidth
            label="Description"
            multiline
            rows={3}
            value={description}
            onChange={(e) => onDescriptionChange?.(e.target.value)}
            placeholder="Optional note or reason for status change…"
            variant="outlined"
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          onClick={onStatusChange}
          variant="contained"
          disabled={!newStatus || processingId === selectedIssue?.id}
          startIcon={processingId === selectedIssue?.id ? <CircularProgress size={20} /> : null}
        >
          Update Status
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default StatusDialog;